/**
 * Helicopter (GDD §3.6: olive-grey body, red band, glass nose, four-blade rotor with blur disc, tail rotor, skids,
 * rocket pods, blinking beacon). Ported verbatim (concept L428–L460).
 */
import { BoxGeometry, CircleGeometry, CylinderGeometry, Group, Mesh, SphereGeometry } from 'three';
import { conceptCapsule, mesh } from '../../world/geometry.ts';
import type { Materials } from '../materials.ts';

const TAU = Math.PI * 2;

export interface HeliParts {
  rotor: Group;
  tail: Group;
  beacon: Mesh;
}

export function buildHeli(M: Materials): Group & { userData: HeliParts } {
  const g = new Group();
  const b = new SphereGeometry(1, 28, 18);
  b.scale(1.5, 1.42, 3.0);
  mesh(b, M.heli, 0, 0, 0, g);
  const band = new SphereGeometry(1, 28, 3, 0, TAU, Math.PI / 2 - 0.13, 0.26);
  band.rotateX(Math.PI / 2);
  band.scale(1.52, 1.44, 3.02);
  mesh(band, M.red, 0, 0, 0.3, g);
  const nose = new SphereGeometry(1, 24, 12, 0, TAU, 0, Math.PI / 2);
  nose.rotateX(-Math.PI / 2);
  nose.scale(1.2, 1.05, 1.35);
  mesh(nose, M.glass, 0, 0.22, -2.1, g);
  const boom = new CylinderGeometry(0.26, 0.52, 7.6, 14);
  boom.rotateX(Math.PI / 2);
  mesh(boom, M.heli, 0, 0.42, 5.9, g);
  mesh(new BoxGeometry(0.14, 2.3, 1.4), M.heli, 0, 1.45, 9.35, g).rotation.x = -0.25;
  mesh(new BoxGeometry(2.6, 0.1, 0.75), M.heli, 0, 0.55, 8.4, g);
  const hump = conceptCapsule(0.62, 2.0, 6, 14);
  hump.rotateX(Math.PI / 2);
  mesh(hump, M.heli, 0, 1.3, 0.8, g);
  for (const s of [1, -1]) {
    const ex = new CylinderGeometry(0.22, 0.26, 0.9, 12);
    ex.rotateX(Math.PI / 2);
    mesh(ex, M.dark, s * 0.55, 1.35, 2.35, g);
    const sk = new CylinderGeometry(0.09, 0.09, 5.6, 8);
    sk.rotateX(Math.PI / 2);
    mesh(sk, M.dark, s * 1.3, -1.8, 0.1, g);
    mesh(new BoxGeometry(0.1, 0.9, 0.12), M.dark, s * 1.18, -1.35, -1.3, g).rotation.z = s * 0.3;
    mesh(new BoxGeometry(0.1, 0.9, 0.12), M.dark, s * 1.18, -1.35, 1.5, g).rotation.z = s * 0.3;
    mesh(new BoxGeometry(2.1, 0.12, 0.8), M.heli, s * 2.0, -0.35, 0.4, g);
    const pod = new CylinderGeometry(0.32, 0.32, 1.9, 12);
    pod.rotateX(Math.PI / 2);
    mesh(pod, M.dark, s * 2.6, -0.62, 0.4, g);
    mesh(new BoxGeometry(0.05, 0.55, 1.1), M.glass, s * 1.45, 0.25, -0.3, g, false);
  }
  mesh(new CylinderGeometry(0.14, 0.16, 0.8, 10), M.dark, 0, 2.0, 0.3, g);
  const rotor = new Group();
  rotor.position.set(0, 2.4, 0.3);
  g.add(rotor);
  mesh(new CylinderGeometry(0.38, 0.38, 0.26, 14), M.dark, 0, 0, 0, rotor);
  for (let i = 0; i < 4; i++) {
    const piv = new Group();
    piv.rotation.y = (i * Math.PI) / 2;
    rotor.add(piv);
    const bl = new BoxGeometry(0.42, 0.05, 7.4);
    bl.translate(0, 0, 3.8);
    mesh(bl, M.blade, 0, 0, 0, piv);
  }
  const disc = new Mesh(new CircleGeometry(7.9, 48), M.disc);
  disc.rotation.x = -Math.PI / 2;
  disc.position.set(0, 2.42, 0.3);
  g.add(disc);
  const tr = new Group();
  tr.position.set(0.25, 1.55, 9.6);
  g.add(tr);
  mesh(new BoxGeometry(0.06, 2.3, 0.22), M.blade, 0, 0, 0, tr);
  mesh(new BoxGeometry(0.06, 0.22, 2.3), M.blade, 0, 0, 0, tr);
  const beacon = mesh(new SphereGeometry(0.16, 10, 8), M.navR, 0, -1.3, 1.6, g, false);
  g.userData = { rotor, tail: tr, beacon } satisfies HeliParts;
  return g as Group & { userData: HeliParts };
}
